import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Building2, 
  ArrowLeft, 
  FileText, 
  Building, 
  User, 
  CreditCard,
  Eye,
  EyeOff,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REGIMENES_FISCALES = [
  "601 - General de Ley Personas Morales",
  "603 - Personas Morales con Fines no Lucrativos",
  "605 - Sueldos y Salarios e Ingresos Asimilados a Salarios",
  "606 - Arrendamiento",
  "607 - Régimen de Enajenación o Adquisición de Bienes",
  "608 - Demás ingresos",
  "610 - Residentes en el Extranjero sin Establecimiento Permanente en México",
  "611 - Ingresos por Dividendos",
  "612 - Personas Físicas con Actividades Empresariales y Profesionales",
  "614 - Ingresos por intereses",
  "615 - Sin obligaciones fiscales",
  "616 - Régimen de los ingresos por obtención de premios",
  "620 - Sociedades Cooperativas de Producción",
  "621 - Incorporación Fiscal",
  "622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras",
  "623 - Opcional para Grupos de Sociedades",
  "624 - Coordinados",
  "625 - Régimen Simplificado de Confianza",
  "626 - Régimen de Actividades Empresariales con ingresos a través de Plataformas Tecnológicas"
];

const ESTADOS_MEXICO = [
  "Aguascalientes", "Baja California", "Baja California Sur", "Campeche", "Chiapas",
  "Chihuahua", "Ciudad de México", "Coahuila", "Colima", "Durango", "Estado de México",
  "Guanajuato", "Guerrero", "Hidalgo", "Jalisco", "Michoacán", "Morelos", "Nayarit",
  "Nuevo León", "Oaxaca", "Puebla", "Querétaro", "Quintana Roo", "San Luis Potosí",
  "Sinaloa", "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán", "Zacatecas"
];

const GIROS = [
  "Construcción",
  "Mantenimiento",
  "Seguridad",
  "Limpieza",
  "Jardinería",
  "Electricidad",
  "Plomería",
  "Sistemas",
  "Transporte",
  "Consultoría",
  "Servicios Profesionales",
  "Otro"
];

const ProveedorRegistro = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);

  const [formData, setFormData] = useState({
    // Paso 1: Datos de acceso
    email: '',
    password: '',
    confirmPassword: '',
    // Paso 2: Datos fiscales
    rfc: '',
    razon_social: '',
    regimen_fiscal: '',
    // Paso 3: Datos de contacto
    nombre_contacto: '',
    telefono: '',
    direccion: '',
    ciudad: '',
    estado: '',
    codigo_postal: '',
    giro: '',
    // Paso 4: Datos bancarios
    banco: '',
    cuenta_bancaria: '',
    clabe: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Requerido';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email inválido';
    if (!formData.password) newErrors.password = 'Requerido';
    else if (formData.password.length < 6) newErrors.password = 'Mínimo 6 caracteres';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Las contraseñas no coinciden';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.rfc) newErrors.rfc = 'Requerido';
    else if (!/^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/i.test(formData.rfc)) newErrors.rfc = 'RFC inválido';
    if (!formData.razon_social) newErrors.razon_social = 'Requerido';
    if (!formData.regimen_fiscal) newErrors.regimen_fiscal = 'Requerido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!formData.nombre_contacto) newErrors.nombre_contacto = 'Requerido';
    if (!formData.telefono) newErrors.telefono = 'Requerido';
    if (!formData.direccion) newErrors.direccion = 'Requerido';
    if (!formData.ciudad) newErrors.ciudad = 'Requerido';
    if (!formData.estado) newErrors.estado = 'Requerido';
    if (!formData.codigo_postal) newErrors.codigo_postal = 'Requerido';
    if (!formData.giro) newErrors.giro = 'Requerido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep4 = () => {
    const newErrors = {};
    if (!formData.banco) newErrors.banco = 'Requerido';
    if (!formData.cuenta_bancaria) newErrors.cuenta_bancaria = 'Requerido';
    if (!formData.clabe) newErrors.clabe = 'Requerido';
    else if (formData.clabe.length !== 18) newErrors.clabe = 'La CLABE debe tener 18 dígitos';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    let valid = false;
    if (step === 1) valid = validateStep1();
    else if (step === 2) valid = validateStep2();
    else if (step === 3) valid = validateStep3();
    
    if (valid) setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep4()) return;

    setLoading(true);
    try {
      const { confirmPassword, ...submitData } = formData;
      await axios.post(`${API_URL}/api/suppliers/register`, submitData);
      setRegistrationComplete(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al registrar';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 shadow-sm max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            ¡Registro Exitoso!
          </h2>
          <p className="text-slate-600 mb-6">
            Su solicitud de registro como proveedor ha sido recibida. 
            Nuestro equipo revisará sus datos y documentos. Le notificaremos 
            por correo electrónico cuando su cuenta sea aprobada.
          </p>
          <div className="bg-amber-50 border border-amber-200 p-4 rounded mb-6">
            <p className="text-amber-800 text-sm">
              <strong>Nota:</strong> Una vez aprobado, podrá iniciar sesión y 
              subir sus facturas durante las ventanas de recepción (del 1 al 5 
              y del 15 al 20 de cada mes).
            </p>
          </div>
          <Button
            onClick={() => navigate('/login')}
            className="w-full bg-slate-900 hover:bg-slate-800"
            data-testid="go-to-login-btn"
          >
            Ir a Iniciar Sesión
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/naave-logo.png" alt="NAAVE" style={{ height: '40px' }} />
          </Link>
          <Link 
            to="/login" 
            className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"
          >
            <ArrowLeft size={16} />
            Volver a Inicio
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                  s < step ? 'bg-green-500 text-white' :
                  s === step ? 'bg-slate-900 text-white' :
                  'bg-slate-200 text-slate-500'
                }`}
              >
                {s < step ? '✓' : s}
              </div>
              {s < 4 && (
                <div className={`w-12 h-1 ${s < step ? 'bg-green-500' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="bg-white shadow-sm p-6 md:p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900 mb-2" data-testid="registration-title">
              Registro de Proveedor
            </h1>
            <p className="text-slate-500">
              {step === 1 && 'Datos de acceso'}
              {step === 2 && 'Datos fiscales'}
              {step === 3 && 'Datos de contacto'}
              {step === 4 && 'Datos bancarios'}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Step 1: Access Data */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 rounded">
                  <User className="text-slate-400" size={20} />
                  <span className="text-sm font-medium text-slate-600">Cree sus credenciales de acceso</span>
                </div>
                
                <div>
                  <Label htmlFor="email">Correo electrónico *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="correo@empresa.com"
                    className={errors.email ? 'border-red-500' : ''}
                    data-testid="input-email"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div>
                  <Label htmlFor="password">Contraseña *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      className={errors.password ? 'border-red-500' : ''}
                      data-testid="input-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirmar contraseña *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    placeholder="Repita su contraseña"
                    className={errors.confirmPassword ? 'border-red-500' : ''}
                    data-testid="input-confirm-password"
                  />
                  {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                </div>
              </div>
            )}

            {/* Step 2: Fiscal Data */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 rounded">
                  <FileText className="text-slate-400" size={20} />
                  <span className="text-sm font-medium text-slate-600">Información fiscal de su empresa</span>
                </div>

                <div>
                  <Label htmlFor="rfc">RFC *</Label>
                  <Input
                    id="rfc"
                    type="text"
                    value={formData.rfc}
                    onChange={(e) => handleChange('rfc', e.target.value.toUpperCase())}
                    placeholder="XAXX010101000"
                    maxLength={13}
                    className={errors.rfc ? 'border-red-500' : ''}
                    data-testid="input-rfc"
                  />
                  {errors.rfc && <p className="text-red-500 text-xs mt-1">{errors.rfc}</p>}
                </div>

                <div>
                  <Label htmlFor="razon_social">Razón Social *</Label>
                  <Input
                    id="razon_social"
                    type="text"
                    value={formData.razon_social}
                    onChange={(e) => handleChange('razon_social', e.target.value)}
                    placeholder="Empresa S.A. de C.V."
                    className={errors.razon_social ? 'border-red-500' : ''}
                    data-testid="input-razon-social"
                  />
                  {errors.razon_social && <p className="text-red-500 text-xs mt-1">{errors.razon_social}</p>}
                </div>

                <div>
                  <Label htmlFor="regimen_fiscal">Régimen Fiscal *</Label>
                  <Select
                    value={formData.regimen_fiscal}
                    onValueChange={(value) => handleChange('regimen_fiscal', value)}
                  >
                    <SelectTrigger className={errors.regimen_fiscal ? 'border-red-500' : ''} data-testid="select-regimen">
                      <SelectValue placeholder="Seleccione régimen" />
                    </SelectTrigger>
                    <SelectContent>
                      {REGIMENES_FISCALES.map((regimen) => (
                        <SelectItem key={regimen} value={regimen}>
                          {regimen}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.regimen_fiscal && <p className="text-red-500 text-xs mt-1">{errors.regimen_fiscal}</p>}
                </div>
              </div>
            )}

            {/* Step 3: Contact Data */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 rounded">
                  <Building className="text-slate-400" size={20} />
                  <span className="text-sm font-medium text-slate-600">Datos de contacto y ubicación</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nombre_contacto">Nombre del contacto *</Label>
                    <Input
                      id="nombre_contacto"
                      type="text"
                      value={formData.nombre_contacto}
                      onChange={(e) => handleChange('nombre_contacto', e.target.value)}
                      placeholder="Juan Pérez"
                      className={errors.nombre_contacto ? 'border-red-500' : ''}
                      data-testid="input-nombre-contacto"
                    />
                    {errors.nombre_contacto && <p className="text-red-500 text-xs mt-1">{errors.nombre_contacto}</p>}
                  </div>

                  <div>
                    <Label htmlFor="telefono">Teléfono *</Label>
                    <Input
                      id="telefono"
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => handleChange('telefono', e.target.value)}
                      placeholder="55 1234 5678"
                      className={errors.telefono ? 'border-red-500' : ''}
                      data-testid="input-telefono"
                    />
                    {errors.telefono && <p className="text-red-500 text-xs mt-1">{errors.telefono}</p>}
                  </div>
                </div>

                <div>
                  <Label htmlFor="direccion">Dirección *</Label>
                  <Input
                    id="direccion"
                    type="text"
                    value={formData.direccion}
                    onChange={(e) => handleChange('direccion', e.target.value)}
                    placeholder="Calle, Número, Colonia"
                    className={errors.direccion ? 'border-red-500' : ''}
                    data-testid="input-direccion"
                  />
                  {errors.direccion && <p className="text-red-500 text-xs mt-1">{errors.direccion}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="ciudad">Ciudad *</Label>
                    <Input
                      id="ciudad"
                      type="text"
                      value={formData.ciudad}
                      onChange={(e) => handleChange('ciudad', e.target.value)}
                      placeholder="Ciudad"
                      className={errors.ciudad ? 'border-red-500' : ''}
                      data-testid="input-ciudad"
                    />
                    {errors.ciudad && <p className="text-red-500 text-xs mt-1">{errors.ciudad}</p>}
                  </div>

                  <div>
                    <Label htmlFor="estado">Estado *</Label>
                    <Select
                      value={formData.estado}
                      onValueChange={(value) => handleChange('estado', value)}
                    >
                      <SelectTrigger className={errors.estado ? 'border-red-500' : ''} data-testid="select-estado">
                        <SelectValue placeholder="Seleccione" />
                      </SelectTrigger>
                      <SelectContent>
                        {ESTADOS_MEXICO.map((estado) => (
                          <SelectItem key={estado} value={estado}>
                            {estado}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.estado && <p className="text-red-500 text-xs mt-1">{errors.estado}</p>}
                  </div>

                  <div>
                    <Label htmlFor="codigo_postal">C.P. *</Label>
                    <Input
                      id="codigo_postal"
                      type="text"
                      value={formData.codigo_postal}
                      onChange={(e) => handleChange('codigo_postal', e.target.value)}
                      placeholder="12345"
                      maxLength={5}
                      className={errors.codigo_postal ? 'border-red-500' : ''}
                      data-testid="input-cp"
                    />
                    {errors.codigo_postal && <p className="text-red-500 text-xs mt-1">{errors.codigo_postal}</p>}
                  </div>
                </div>

                <div>
                  <Label htmlFor="giro">Giro *</Label>
                  <Select
                    value={formData.giro}
                    onValueChange={(value) => handleChange('giro', value)}
                  >
                    <SelectTrigger className={errors.giro ? 'border-red-500' : ''} data-testid="select-giro">
                      <SelectValue placeholder="Seleccione giro" />
                    </SelectTrigger>
                    <SelectContent>
                      {GIROS.map((giro) => (
                        <SelectItem key={giro} value={giro}>
                          {giro}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.giro && <p className="text-red-500 text-xs mt-1">{errors.giro}</p>}
                </div>
              </div>
            )}

            {/* Step 4: Bank Data */}
            {step === 4 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 rounded">
                  <CreditCard className="text-slate-400" size={20} />
                  <span className="text-sm font-medium text-slate-600">Datos bancarios para pagos</span>
                </div>

                <div>
                  <Label htmlFor="banco">Banco *</Label>
                  <Input
                    id="banco"
                    type="text"
                    value={formData.banco}
                    onChange={(e) => handleChange('banco', e.target.value)}
                    placeholder="BBVA, Santander, Banorte..."
                    className={errors.banco ? 'border-red-500' : ''}
                    data-testid="input-banco"
                  />
                  {errors.banco && <p className="text-red-500 text-xs mt-1">{errors.banco}</p>}
                </div>

                <div>
                  <Label htmlFor="cuenta_bancaria">Número de cuenta *</Label>
                  <Input
                    id="cuenta_bancaria"
                    type="text"
                    value={formData.cuenta_bancaria}
                    onChange={(e) => handleChange('cuenta_bancaria', e.target.value)}
                    placeholder="1234567890"
                    className={errors.cuenta_bancaria ? 'border-red-500' : ''}
                    data-testid="input-cuenta"
                  />
                  {errors.cuenta_bancaria && <p className="text-red-500 text-xs mt-1">{errors.cuenta_bancaria}</p>}
                </div>

                <div>
                  <Label htmlFor="clabe">CLABE Interbancaria *</Label>
                  <Input
                    id="clabe"
                    type="text"
                    value={formData.clabe}
                    onChange={(e) => handleChange('clabe', e.target.value.replace(/\D/g, ''))}
                    placeholder="18 dígitos"
                    maxLength={18}
                    className={errors.clabe ? 'border-red-500' : ''}
                    data-testid="input-clabe"
                  />
                  {errors.clabe && <p className="text-red-500 text-xs mt-1">{errors.clabe}</p>}
                </div>

                <div className="bg-blue-50 border border-blue-200 p-4 rounded mt-6">
                  <p className="text-blue-800 text-sm">
                    <strong>Importante:</strong> Al enviar su registro, acepta que sus datos 
                    serán verificados por el administrador. Recibirá un correo de confirmación 
                    cuando su cuenta sea aprobada.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {step > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  data-testid="btn-back"
                >
                  Anterior
                </Button>
              ) : (
                <div />
              )}

              {step < 4 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="btn-next"
                >
                  Siguiente
                </Button>
              ) : (
                <Button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700"
                  disabled={loading}
                  data-testid="btn-submit"
                >
                  {loading ? 'Enviando...' : 'Enviar Registro'}
                </Button>
              )}
            </div>
          </form>
        </div>

        <p className="text-center text-slate-500 text-sm mt-6">
          ¿Ya tiene cuenta?{' '}
          <Link to="/login" className="text-slate-900 hover:underline">
            Iniciar sesión
          </Link>
        </p>
      </main>
    </div>
  );
};

export default ProveedorRegistro;
